import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    getGroup,
    GroupSession,
    GroupState,
    postGroupCancel,
    postGroupItem,
    postGroupItemRemove,
    postGroupJoin,
    postGroupLeave,
    postGroupLock,
    postGroupStart,
} from '../../services/api/group';
import { Item } from '../site/site-context';
import {
    forgetGroup,
    LineChanges,
    rememberGroup,
    storedGroup,
    StoredGroup,
    toGroupLine,
} from '../../services/groupOrder';
import { iLog } from '../../index';
import { GroupContext, GroupContextData, GroupEnd } from './group-context';

// how often everyone in a group order hears about what the others have done
export const GROUP_POLL_MS = 4000;

// the API says so when a group order has gone, been ended, or you've been taken out of it
const GONE_CODES = [880, 881, 884];

const GroupContextProvider = ({ children }: { children: JSX.Element }) => {
    const [session, setSession] = useState<StoredGroup | null>(() => storedGroup());
    const [state, setState] = useState<GroupState | null>(null);
    const [ended, setEnded] = useState<GroupEnd | null>(null);

    const [preview, setPreview] = useState<GroupState | null>(null);
    const [previewFailed, setPreviewFailed] = useState(false);

    const location = useLocation();

    // changes we've sent that haven't been answered yet: what we hear back in the meantime is out of date
    const pending = useRef(0);
    const queue = useRef<Promise<unknown>>(Promise.resolve());
    const sessionRef = useRef(session);
    sessionRef.current = session;
    const revisionRef = useRef<string | undefined>(undefined);
    const statusRef = useRef<string | undefined>(undefined);

    const apply = useCallback((incoming: GroupState) => {
        const status = incoming.group.status;

        // nothing to redraw when nothing has changed
        if (incoming.revision !== revisionRef.current || status !== statusRef.current) {
            revisionRef.current = incoming.revision;
            statusRef.current = status;
            setState(incoming);
        }

        if (status === 'cancelled') setEnded('cancelled');
        if (status === 'placed') setEnded('placed');
    }, []);

    const clear = useCallback(() => {
        forgetGroup();
        setSession(null);
        setState(null);
        setEnded(null);
        revisionRef.current = undefined;
        statusRef.current = undefined;
    }, []);

    const refresh = useCallback(() => {
        const current = sessionRef.current;

        if (!current || pending.current > 0) return Promise.resolve();

        return getGroup(current.token, current.participantToken)
            .then((incoming) => {
                // (what came back is only good if nothing was sent while it was on its way)
                if (pending.current === 0 && sessionRef.current?.token === current.token) apply(incoming);
            })
            .catch((error: any) => {
                if (GONE_CODES.includes(error?.code)) setEnded('cancelled');
                else iLog('Group order poll failed', error);
            });
    }, [apply]);

    // everybody in a group order keeps up with it
    useEffect(() => {
        if (!session || ended) return;

        refresh();

        const timer = setInterval(() => {
            if (!document.hidden) refresh();
        }, GROUP_POLL_MS);

        return () => clearInterval(timer);
    }, [session?.token, session?.participantToken, ended, refresh]);

    // the lead's order is over once it has gone through: it carries on from the confirmation page
    useEffect(() => {
        if (ended === 'placed' && session?.isLead) clear();
    }, [ended, session?.isLead, clear]);

    const begin = (data: GroupSession, siteUid: string, isLead: boolean) => {
        const next: StoredGroup = {
            token: data.token ?? '',
            participantToken: data.participant_token ?? '',
            siteUid,
            isLead,
        };

        rememberGroup(next);
        revisionRef.current = undefined;
        statusRef.current = undefined;
        setEnded(null);
        setSession(next);
        apply(data);

        return next;
    };

    const start = async (siteUid: string, name: string, items: Item[] = []) => {
        const data = await postGroupStart(siteUid, name);

        const next = begin(data, siteUid, true);

        // whatever was in the basket before is the lead's to begin with
        send(next, { upserts: items.filter((item) => item.basketIndex), removals: [] });
    };

    const join = async (token: string, name: string) => {
        const data = await postGroupJoin(token, name);

        begin(data, data.group.site_uid, false);
    };

    const leave = async () => {
        const current = sessionRef.current;

        if (current) await postGroupLeave(current.token, current.participantToken).catch(() => undefined);

        clear();
    };

    const cancel = async () => {
        const current = sessionRef.current;

        if (current) await postGroupCancel(current.token, current.participantToken).catch(() => undefined);

        clear();
    };

    const setLocked = async (locked: boolean) => {
        const current = sessionRef.current;

        if (!current || !current.isLead || ended) return;

        // (nothing to say when it's as it should be, though until we've heard we don't know)
        if (statusRef.current !== undefined && (statusRef.current === 'locked') === locked) return;

        statusRef.current = locked ? 'locked' : 'open';

        try {
            apply(await postGroupLock(current.token, current.participantToken, locked));
        } catch (error: any) {
            if (GONE_CODES.includes(error?.code)) {
                setEnded('cancelled');
            } else {
                // it didn't take, so what we thought it was isn't to be trusted: ask
                statusRef.current = undefined;
                revisionRef.current = undefined;
                await refresh();
            }
        }
    };

    // what happened to the basket on this device is told to the API one change at a time, in order
    const send = (current: StoredGroup | null, changes: LineChanges) => {
        if (!current || (changes.upserts.length === 0 && changes.removals.length === 0)) return;

        pending.current++;

        queue.current = queue.current.then(async () => {
            try {
                for (const item of changes.upserts) {
                    const line = toGroupLine(item);
                    await postGroupItem(current.token, current.participantToken, line.lineId, line.order, line.display);
                }

                for (const lineId of changes.removals) {
                    await postGroupItemRemove(current.token, current.participantToken, lineId);
                }
            } catch (error: any) {
                // (it said why: too much, no longer available, locked..)
                if (GONE_CODES.includes(error?.code)) setEnded('cancelled');
            } finally {
                pending.current--;

                // whatever the API made of it is the truth
                if (pending.current === 0) {
                    revisionRef.current = undefined;
                    await refresh();
                }
            }
        });
    };

    const sendChanges = (changes: LineChanges) => send(sessionRef.current, changes);

    const fetchPreview = useCallback((token: string) => {
        setPreviewFailed(false);

        getGroup(token)
            .then((incoming) => {
                setPreview(incoming);
                setPreviewFailed(incoming.group.status !== 'open' && incoming.group.status !== 'locked');
            })
            .catch(() => {
                setPreview(null);
                setPreviewFailed(true);
            });
    }, []);

    // a guest is done with the group order once they leave its confirmation page
    const previousPath = useRef(location.pathname);

    useEffect(() => {
        const was = previousPath.current;
        previousPath.current = location.pathname;

        if (was === '/group-confirmation' && location.pathname !== was && sessionRef.current && !sessionRef.current.isLead) {
            clear();
        }
    }, [location.pathname, clear]);

    // the group order doesn't survive the operator's homepage, where everything starts again
    useEffect(() => {
        if (location.pathname !== '/' || !sessionRef.current) return;

        if (sessionRef.current.isLead) cancel();
        else leave();
    }, [location.pathname]);

    const active = !!session && !ended;
    const lines = state?.items ?? [];

    const value: GroupContextData = {
        active,
        isLead: !!session?.isLead,
        locked: active && state?.group.status === 'locked',

        session,
        state,
        status: state?.group.status ?? null,
        lines,
        revision: state?.revision,
        host: state?.host ?? null,
        name: state?.me?.name ?? null,
        guestCount: state?.guests?.length ?? 0,
        ended,

        preview,
        previewFailed,
        fetchPreview,

        start,
        join,
        leave,
        cancel,
        setLocked,
        sendChanges,
        clear,
    };

    return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
};

export default GroupContextProvider;
